import { ArdoqId, ArrowType } from '@ardoq/api-types';
import { modelOperations } from '@ardoq/core';
import { SimpleReferenceModel } from '@ardoq/data-model';
import { isReferenceIdIncludedInContextByFilter } from '@ardoq/reference-interface';
import { isTruthy } from '@ardoq/common-helpers';
import { geometry } from '@ardoq/math';
import { colors } from '@ardoq/design-tokens';
import {
  ComponentDetails,
  ComponentModelData,
  ReferenceDetails,
  ReferenceModelData,
} from './types';
import {
  calculateOffsetInDeg,
  getAngle,
  getPoint,
} from './utils/calculatePosition';
import { CIRCLE_RADIUS_PORTION, CONTROL_POINT, ICON_RADIUS } from './consts';
import { MARKER_PROPS } from '@ardoq/icons';
import { PointAndAngle, Rectangle } from '@ardoq/graph';

export const getReferenceIdsByComponentId = (
  componentId: ArdoqId,
  referenceMap: Map<string, SimpleReferenceModel>
) =>
  Array.from(referenceMap.keys()).filter(
    referenceId =>
      isReferenceIdIncludedInContextByFilter(referenceId) &&
      (referenceMap.get(referenceId)!.targetId === componentId ||
        referenceMap.get(referenceId)!.sourceId === componentId)
  );

export const getConnectedComponentIds = (
  componentIds: ArdoqId[],
  referenceMap: Map<string, SimpleReferenceModel>
) => {
  const connectedComponentIds = new Set<ArdoqId>();

  for (const id of componentIds) {
    const referenceIds = getReferenceIdsByComponentId(id, referenceMap);

    for (const referenceId of referenceIds) {
      const ref = referenceMap.get(referenceId);
      const referenceComponentIds = [ref?.sourceId, ref?.targetId].filter(
        isTruthy
      );

      referenceComponentIds.forEach(
        connectedComponentIds.add,
        connectedComponentIds
      );
    }
  }

  return [...connectedComponentIds];
};

type FilterReferenceToRenderIdsArgs = {
  referenceIds: ArdoqId[];
  referenceMap: Map<string, SimpleReferenceModel>;
  componentToDisplayIdsSet: Set<ArdoqId>;
  selectedComponentId?: ArdoqId;
};

export const filterReferenceToRenderIds = ({
  referenceMap,
  referenceIds,
  componentToDisplayIdsSet,
  selectedComponentId,
}: FilterReferenceToRenderIdsArgs) =>
  referenceIds.filter(referenceId => {
    if (!isReferenceIdIncludedInContextByFilter(referenceId)) {
      return false;
    }

    const hasSourceId = componentToDisplayIdsSet.has(
      referenceMap.get(referenceId)!.sourceId
    );
    const hasTargetId = componentToDisplayIdsSet.has(
      referenceMap.get(referenceId)!.targetId
    );

    return selectedComponentId
      ? hasSourceId || hasTargetId
      : hasSourceId && hasTargetId;
  });

export const collectReferenceDetails = ({
  referenceId,
  reference,
  referenceType,
  cssColors,
}: ReferenceModelData): ReferenceDetails | null => {
  const sourceId = reference?.source;
  const targetId = reference?.target;
  const referenceTypeId = reference?.type ?? null;
  const rootWorkspaceId = reference?.rootWorkspace;

  if (!sourceId || !targetId || referenceTypeId === null || !rootWorkspaceId) {
    return null;
  }

  return {
    referenceId,
    sourceId,
    targetId,
    lineBeginning: referenceType?.lineBeginning ?? ArrowType.NONE,
    lineEnding: referenceType?.lineEnding ?? ArrowType.NONE,
    strokeDasharray: modelOperations.lineTypeToStrokeDasharray(
      referenceType?.line
    ),
    // SVG path is a Bezier line, not a closed shape. A non-'none' fill
    // closes the curve and renders it as a filled wedge — see ARD-28006.
    fill: 'none',
    stroke: cssColors?.stroke ?? colors.black950,
  };
};

/** @returns the intersection point of the two rays. coordinates may be NaN or infinite in the case of rays which do not intersect, e.g. if they are parallel or diverging. */
const rayIntersection = (
  [x0, y0, a0]: PointAndAngle,
  [x1, y1, a1]: PointAndAngle
) => {
  if (a0 === HEADING_UP || a0 === HEADING_DOWN) {
    return [x0, Math.tan(a1) * (x0 - x1) + y1];
  } else if (a1 === HEADING_UP || a1 === HEADING_DOWN) {
    return [x1, Math.tan(a0) * (x1 - x0) + y0];
  }
  const m0 = Math.tan(a0);
  const m1 = Math.tan(a1);
  const x = (m0 * x0 - m1 * x1 - (y0 - y1)) / (m0 - m1);
  return [x, m0 * (x - x0) + y0];
};

const HEADING_RIGHT = 0;
const HEADING_DOWN = (3 * Math.PI) / 2;
const HEADING_LEFT = Math.PI;
const HEADING_UP = Math.PI / 2;

/** finds the shortest distance from the given point and angle to the border of the given rectangle. */
export const shortestDistanceToBounds = (
  radial: PointAndAngle,
  [boundsX, boundsY, boundsWidth, boundsHeight]: Rectangle
) => {
  const boundsRight = boundsX + boundsWidth;
  const boundsBottom = boundsY + boundsHeight;

  const topRay: PointAndAngle = [boundsX, boundsY, HEADING_RIGHT];
  const rightRay: PointAndAngle = [boundsRight, boundsY, HEADING_DOWN];
  const bottomRay: PointAndAngle = [boundsRight, boundsBottom, HEADING_LEFT];
  const leftRay: PointAndAngle = [boundsX, boundsBottom, HEADING_UP];

  const intersections = [topRay, rightRay, bottomRay, leftRay].map(borderRay =>
    rayIntersection(radial, borderRay)
  );

  const [x, y] = radial;
  const result = Math.min(
    ...intersections
      .map(([intersectionX, interSectionY]) =>
        geometry.distanceFourArgs(x, y, intersectionX, interSectionY)
      )
      .filter(isFinite)
  );

  return result;
};

const toComponentDetails = (
  componentModelData: ComponentModelData,
  componentsCount: number,
  componentIndex: number
): ComponentDetails => {
  const rotationOffset = calculateOffsetInDeg(componentsCount);
  const rotationInDegrees = Math.round(
    (((componentIndex / componentsCount) * 360) % 360) + rotationOffset
  );
  const shouldFlipLabel = rotationInDegrees > 90 && rotationInDegrees < 270;

  return {
    ...componentModelData,
    rotationInDegrees,
    shouldFlipLabel,
  };
};

export const getComponentsToDisplay = (
  componentModelData: ComponentModelData[]
): ComponentDetails[] => {
  return componentModelData.map((data, index) =>
    toComponentDetails(data, componentModelData.length, index)
  );
};

export const calculateReferencesPathPoints = (
  references: ReferenceDetails[],
  components: ComponentDetails[],
  viewBounds: Rectangle
) => {
  const componentsCount = components.length;
  const componentIdAndItsIndexMap: Record<ArdoqId, number> = components.reduce<
    Record<ArdoqId, number>
  >((previousValue, currentValue, index) => {
    previousValue[currentValue.id] = index;
    return previousValue;
  }, {});

  return references.map(reference =>
    calculateReferencePathPoints(
      reference,
      componentsCount,
      componentIdAndItsIndexMap,
      viewBounds
    )
  );
};

const calculateReferencePathPoints = (
  reference: ReferenceDetails,
  componentsCount: number,
  componentIdAndItsIndexMap: Record<ArdoqId, number>,
  [, , viewWidth, viewHeight]: Rectangle
) => {
  const { sourceId, targetId, lineBeginning, lineEnding } = reference;
  const startIndex = componentIdAndItsIndexMap[sourceId];
  const endIndex = componentIdAndItsIndexMap[targetId];
  const iconRadiusWithBeginningMarkerSize =
    MARKER_PROPS[lineBeginning].refX + ICON_RADIUS;

  const iconRadiusWithEndMarkerSize =
    MARKER_PROPS[lineEnding].markerWidth -
    MARKER_PROPS[lineEnding].refX +
    ICON_RADIUS;

  const startAngle = getAngle(startIndex, componentsCount);
  const endAngle = getAngle(endIndex, componentsCount);

  const circleRadius = Math.min(viewWidth, viewHeight) * CIRCLE_RADIUS_PORTION;

  const startPoint = getPoint(
    startAngle,
    circleRadius - iconRadiusWithBeginningMarkerSize
  );
  const startControlPoint = getPoint(startAngle, circleRadius * CONTROL_POINT);
  const endControlPoint = getPoint(endAngle, circleRadius * CONTROL_POINT);
  const endPoint = getPoint(
    endAngle,
    circleRadius - iconRadiusWithEndMarkerSize
  );

  return {
    ...reference,
    startPoint,
    startControlPoint,
    endPoint,
    endControlPoint,
  };
};
